import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Loader2, MessageSquare } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface ReviewEntry {
  id: string;
  name: string | null;
  rating: number;
  message: string | null;
  created_at: string;
}

export default function ReviewsPage() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<ReviewEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("feedback")
        .select("id, name, rating, message, created_at")
        .eq("status", "approved")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setReviews((data ?? []) as ReviewEntry[]);
    } catch (err) {
      const msg =
        (err as { message?: string })?.message ??
        (err instanceof Error ? err.message : null) ??
        String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStars = (rating: number) => {
    const arr: React.ReactNode[] = [];
    for (let i = 1; i <= 5; i++) {
      arr.push(
        <Star
          key={i}
          size={16}
          className={
            i <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"
          }
        />
      );
    }
    return <span className="inline-flex items-center gap-0.5">{arr}</span>;
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-zinc-950 text-zinc-300 antialiased">
      <div className="pointer-events-none fixed inset-0 z-[-1] overflow-hidden">
        <div className="absolute left-1/4 top-1/4 h-[500px] w-[500px] rounded-full bg-purple-900/30 blur-[120px]" />
        <div className="absolute right-1/4 top-1/2 h-[400px] w-[400px] rounded-full bg-pink-900/20 blur-[100px]" />
        <div className="absolute bottom-1/4 left-1/2 h-[350px] w-[350px] rounded-full bg-violet-900/25 blur-[100px]" />
      </div>

      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl">
        <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 md:px-8">
          <Link
            to="/landing"
            className="flex items-center gap-2.5 font-semibold tracking-tight text-white"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25">
              <MessageSquare className="h-5 w-5" />
            </span>
            <span>StudyDo</span>
          </Link>
          <Link to="/landing">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-zinc-400 hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("reviewsPage.backToLanding")}
            </Button>
          </Link>
        </nav>
      </header>

      <main className="relative px-6 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-16 text-center md:mb-20">
            <h1 className="text-3xl font-bold tracking-tighter text-white md:text-4xl lg:text-5xl">
              {t("reviewsPage.title")}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-zinc-400 md:text-lg">
              {t("reviewsPage.subtitle")}
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
              <Loader2 size={32} className="animate-spin text-purple-400" />
              <p className="text-sm text-zinc-400">{t("reviewsPage.loading")}</p>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-8 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
              <MessageSquare className="h-12 w-12 text-zinc-500" />
              <p className="font-medium text-zinc-200">
                {t("reviewsPage.emptyState")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {reviews.map((review) => (
                <Card
                  key={review.id}
                  className="border border-white/10 bg-white/5 backdrop-blur-xl transition-all duration-300 hover:border-purple-500/30"
                >
                  <CardContent className="flex flex-col p-6 md:p-8">
                    <div className="mb-4">{renderStars(review.rating)}</div>
                    <p className="mb-6 flex-1 text-sm leading-relaxed text-zinc-300">
                      &ldquo;{review.message || t("reviewsPage.noMessage")}&rdquo;
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <span className="font-semibold text-white">
                        {review.name || t("reviewsPage.anonymous")}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
